import React, { useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { StartupInterestedInvestorItem } from '../../types/startupDashboard';
import type { InvestorProfile as InvestorProfileType } from '../../types/firestore';
import InvestorProfile from '../profile/InvestorProfile';
import profileGrey from '../../assets/profile_icon__grey-correct_.png';
import backArrow from '../../assets/back-arrow_grey_2.png';
import DealTracker from '../DealTracker';
import ContactStartupMessagePopup from '../profile/ContactStartupMessagePopup';
import lightBulb from '../../assets/light_bulb_1.png';
import { PrairiScrollbar } from '../profile/PrairiScrollbar';
import Button from '../Button';
import {
  ACTION_LABELS,
  getNextStartupDealStatus,
  type DealStatus,
  USER_TYPES,
} from '../../constants/dealTracker';
import { useDealTracking } from '../../utils/useDealTracking';
import type {
  MessageAttachmentInput,
  SendMessagePayload,
  SendMessageResponse,
} from '../../types/messages';

type ResponseAnswer = 'yes' | 'not-sure' | 'no';

interface InterestedInvestorCardProps {
  investor: StartupInterestedInvestorItem;
  onResponseSubmitted?: (investorId: string) => void;
  selectedInvestorId?: string;
  selectionTimestamp?: number;
  isModalMode?: boolean;
  onCloseModal?: () => void;
}

const RADIO_OPTIONS: { value: ResponseAnswer; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'not-sure', label: 'Not sure' },
  { value: 'no', label: 'No' },
];

const InterestedInvestorCard: React.FC<InterestedInvestorCardProps> = ({
  investor,
  onResponseSubmitted,
  selectedInvestorId,
  selectionTimestamp,
  isModalMode = false,
  onCloseModal,
}) => {
  const cardRef = React.useRef<HTMLDivElement>(null);
  const profileContainerRef = React.useRef<HTMLDivElement>(null);
  const [knowsInvestor, setKnowsInvestor] = useState<ResponseAnswer | null>(null);
  const [pitchedRecently, setPitchedRecently] = useState<ResponseAnswer | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMatch = Boolean(
    selectedInvestorId &&
      (selectedInvestorId === investor.investorId ||
        selectedInvestorId === investor.id ||
        (investor.investorName &&
          (selectedInvestorId.toLowerCase() === investor.investorName.toLowerCase() ||
            selectedInvestorId.toLowerCase().includes(investor.investorName.toLowerCase()) ||
            investor.investorName.toLowerCase().includes(selectedInvestorId.toLowerCase()))))
  );
  const [hasResponded, setHasResponded] = useState(investor.hasResponded);
  const [revealed, setRevealed] = useState(investor.hasResponded);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);

  useEffect(() => {
    if (!selectionTimestamp || !isMatch) return;
    setRevealed(true);
    setHasResponded(true);
    setIsProfileOpen(true);
    setIsHighlighted(true);
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    if (profileContainerRef.current) {
      profileContainerRef.current.scrollTop = 0;
    }
    const timer = setTimeout(() => setIsHighlighted(false), 2200);
    return () => clearTimeout(timer);
  }, [selectionTimestamp, isMatch]);

  useEffect(() => {
    if (!isMatch) return;
    setRevealed(true);
    setHasResponded(true);
    setIsProfileOpen(true);
  }, [selectedInvestorId, isMatch]);
  const [showContactMessagePopup, setShowContactMessagePopup] = useState(false);
  const [messageSending, setMessageSending] = useState(false);

  const [investorProfile, setInvestorProfile] = useState<InvestorProfileType | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const {
    dealStatus,
    isLoading: dealStatusLoading,
    isUpdating: dealStatusUpdating,
    refreshStatus: refreshDealStatus,
    updateDealStatus,
  } = useDealTracking({ investorId: investor.investorId });

  useEffect(() => {
    if (!hasResponded) return;

    let cancelled = false;
    const loadProfile = async () => {
      setProfileLoading(true);
      try {
        const functions = getFunctions();
        const getProfile = httpsCallable<
          { userId: string },
          { success: boolean; profile?: InvestorProfileType; error?: string }
        >(functions, 'getInvestorProfile');
        const result = await getProfile({ userId: investor.investorId });
        if (cancelled) return;
        if (result.data.success && result.data.profile) {
          setInvestorProfile(result.data.profile);
        }
      } catch {
        // Profile load is best-effort; card still renders with basic info
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [hasResponded, investor.investorId]);

  const activeProfile: InvestorProfileType = useMemo(
    () =>
      investorProfile
        ? {
            ...investorProfile,
            // Prefer the loaded profile. Do not keep a stale list URL after the photo was removed.
            profilePictureUrl:
              typeof (investorProfile as { profilePictureUrl?: string }).profilePictureUrl ===
                'string' &&
              (investorProfile as { profilePictureUrl?: string }).profilePictureUrl!.trim()
                ? (investorProfile as { profilePictureUrl?: string }).profilePictureUrl!.trim()
                : undefined,
          }
        : ({
            id: investor.investorId,
            userId: investor.investorId,
            companyName: investor.companyName || '',
            country: investor.country || 'United States',
            expertise: ['Expertise in the field'],
            previousInvestments: 'Lucy, Sally',
            paysFee: true,
            profilePictureUrl: investor.profilePictureUrl,
          } as any),
    [investorProfile, investor]
  );

  const listPhotoUrl =
    typeof investor.profilePictureUrl === 'string' ? investor.profilePictureUrl.trim() : '';
  // Once the profile is loaded, only trust its photo (not a stale list URL).
  const collapsedPhotoUrl = investorProfile
    ? typeof (investorProfile as { profilePictureUrl?: string }).profilePictureUrl === 'string'
      ? (investorProfile as { profilePictureUrl?: string }).profilePictureUrl!.trim()
      : ''
    : listPhotoUrl;
  const [collapsedPhotoFailed, setCollapsedPhotoFailed] = useState(false);
  useEffect(() => {
    setCollapsedPhotoFailed(false);
  }, [collapsedPhotoUrl]);
  const showCollapsedPhoto = Boolean(collapsedPhotoUrl) && !collapsedPhotoFailed;

  const canSubmit = knowsInvestor !== null && pitchedRecently !== null && !submitting;

  const rawBaseStatus = (investor.startupStatus as DealStatus) || 'favorited';
  const baseStatus = rawBaseStatus === 'not-contacted' ? 'favorited' : rawBaseStatus;
  const resolvedDealStatus: DealStatus =
    dealStatus === 'not-contacted' ? baseStatus : dealStatus || baseStatus;
  const effectiveDealStatus: DealStatus =
    investor.investorHasReachedOut && resolvedDealStatus === 'favorited'
      ? 'contacted'
      : resolvedDealStatus || 'favorited';

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      const functions = getFunctions();
      const submitFn = httpsCallable<
        { investorId: string; knowsInvestor: ResponseAnswer; pitchedRecently: ResponseAnswer },
        { success: boolean; error?: string }
      >(functions, 'submitStartupResponse');

      const result = await submitFn({
        investorId: investor.investorId,
        knowsInvestor,
        pitchedRecently,
      });

      if (result.data.success) {
        setHasResponded(true);
        onResponseSubmitted?.(investor.investorId);
      } else {
        setError(result.data.error || 'Failed to submit response');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendInvestorMessage = async (
    message: string,
    attachment?: MessageAttachmentInput | null
  ) => {
    setMessageSending(true);
    try {
      const functions = getFunctions();
      const sendMessage = httpsCallable<SendMessagePayload, SendMessageResponse>(
        functions,
        'sendMessage'
      );
      const result = await sendMessage({
        recipientId: investor.investorId,
        body: message,
        attachment: attachment ?? null,
      });
      const data = result.data;
      if (!data.success) {
        setError(data.error || 'Could not send message');
        return false;
      }
      // Keep the message box open so the new line shows in the thread.
      updateDealStatus('contacted')
        .then(() => refreshDealStatus())
        .catch((err: { message?: string }) => {
          setError(err?.message || 'Could not update deal status');
        });
      return true;
    } catch (err: any) {
      setError(err?.message || 'Could not send message');
      return false;
    } finally {
      setMessageSending(false);
    }
  };

  const handleStartupTrackerAction = async () => {
    if (effectiveDealStatus === 'favorited' && !investor.investorHasReachedOut) {
      setShowContactMessagePopup(true);
      return;
    }

    const nextStatus = getNextStartupDealStatus(effectiveDealStatus);
    if (!nextStatus) return;
    try {
      await updateDealStatus(nextStatus);
      await refreshDealStatus();
    } catch (err: any) {
      setError(err?.message || 'Could not update deal status');
    }
  };

  const handleRewindDealStep = async (step: DealStatus) => {
    if (dealStatusUpdating || step === effectiveDealStatus) return;
    try {
      await updateDealStatus(step);
      await refreshDealStatus();
    } catch (err: any) {
      setError(err?.message || 'Could not update deal status');
    }
  };

  if (hasResponded) {
    const isSelected = Boolean(selectedInvestorId && selectedInvestorId === investor.investorId);

    return (
      <div
        ref={cardRef}
        className="relative rounded-3xl bg-[#333333] shadow-2xl transition-all duration-300 overflow-hidden"
      >
        {!isProfileOpen && (
          <div className="p-4 flex items-center gap-3 relative">
            {showCollapsedPhoto ? (
              <img
                src={collapsedPhotoUrl}
                alt="Investor"
                onError={() => setCollapsedPhotoFailed(true)}
                className="h-10 w-10 flex-shrink-0 rounded-full border border-[#444444] object-cover"
              />
            ) : (
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#444444] bg-[#333333]">
                <img
                  src={profileGrey}
                  alt=""
                  aria-hidden
                  className="h-[84%] w-[84%] translate-y-[14%] object-contain"
                />
              </span>
            )}
            <div className="text-left space-y-0.5 flex-1 min-w-0 pr-20">
              <p className="text-sm font-bold text-white break-words">{investor.investorName}</p>
              {investor.companyName && (
                <p className="text-xs text-gray-400 truncate">{investor.companyName}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsProfileOpen(true)}
              className="absolute top-4 right-4 text-xs text-emerald-400 underline hover:text-white transition-colors"
            >
              View profile
            </button>
          </div>
        )}

        {isProfileOpen ? (
          <div className="flex flex-col">
            <div className={isModalMode ? 'relative min-h-0 flex-1' : undefined}>
              <div
                ref={profileContainerRef}
                className={
                  isModalMode
                    ? 'prairi-hide-native-scrollbar max-h-[60vh] overflow-y-auto lg:max-h-[65vh]'
                    : undefined
                }
              >
                <div className="relative px-5 pt-6 pb-3 sm:px-7 text-left">
                  <button
                    type="button"
                    onClick={() => {
                      if (isModalMode && onCloseModal) {
                        onCloseModal();
                      } else {
                        setIsProfileOpen(false);
                      }
                    }}
                    className="absolute top-4 right-4 sm:top-5 sm:right-5 z-10 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white transition-colors rounded-full"
                    aria-label={isModalMode ? 'Close modal' : 'Close profile'}
                  >
                    {isModalMode ? (
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    ) : (
                      <img src={backArrow} alt="" className="w-8 h-8 object-contain" aria-hidden />
                    )}
                  </button>

                  <InvestorProfile
                    profile={activeProfile}
                    startupDashboard
                    investorName={investor.investorName}
                    onContactClick={() => setShowContactMessagePopup(true)}
                  />
                </div>
              </div>
              {isModalMode ? (
                <PrairiScrollbar
                  scrollRef={profileContainerRef}
                  hoverRootRef={cardRef}
                  thumbHeight={40}
                  thumbWidth={6}
                  trackInsetY={10}
                />
              ) : null}
            </div>

            <div className="shrink-0 bg-[#333333] rounded-b-3xl rounded-t-none shadow-[inset_0_4px_8px_rgba(0,0,0,0.25)] px-4 py-4 sm:px-5">
              <DealTracker
                userType={USER_TYPES.startup}
                status={effectiveDealStatus}
                primaryActionLabel={ACTION_LABELS.startup[effectiveDealStatus]}
                onPrimaryActionClick={handleStartupTrackerAction}
                onCompletedStepClick={handleRewindDealStep}
                primaryActionLoading={dealStatusUpdating || messageSending}
                primaryActionDisabled={dealStatusLoading || dealStatusUpdating || messageSending}
              />
              {!investor.investorHasReachedOut && effectiveDealStatus === 'favorited' && (
                <div className="mt-2 text-center">
                  <p className="inline-flex items-center justify-center gap-1.5 text-[11px] text-gray-400">
                    <img
                      src={lightBulb}
                      alt=""
                      className="h-3.5 w-3.5 object-contain"
                      aria-hidden
                    />
                    TIP: Give the investor a chance to reach out first
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2 px-3 pb-4 sm:px-4">
            <DealTracker
              userType={USER_TYPES.startup}
              status={effectiveDealStatus}
              onCompletedStepClick={handleRewindDealStep}
            />
          </div>
        )}
        <ContactStartupMessagePopup
          isOpen={showContactMessagePopup}
          onClose={() => setShowContactMessagePopup(false)}
          onSend={handleSendInvestorMessage}
          isSending={messageSending}
          recipientId={investor.investorId}
          startupName={investor.companyName || investor.investorName}
          thumbnailUrl={collapsedPhotoUrl || undefined}
          founderLine={investor.companyName ? `Investor: ${investor.investorName}` : undefined}
        />
      </div>
    );
  }

  if (!revealed) {
    return (
      <div className="relative rounded-xl bg-[#1A1A1A] p-4 space-y-3 overflow-hidden">
        <div className="blur pointer-events-none select-none">
          <div className="flex items-center gap-3 relative">
            {listPhotoUrl ? (
              <img
                src={listPhotoUrl}
                alt="Investor"
                className="h-10 w-10 flex-shrink-0 rounded-full border border-[#444444] object-cover"
              />
            ) : (
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#444444] bg-[#1A1A1A]">
                <img
                  src={profileGrey}
                  alt=""
                  aria-hidden
                  className="h-[84%] w-[84%] translate-y-[14%] object-contain"
                />
              </span>
            )}
            <div className="text-left space-y-0.5 flex-1 min-w-0 pr-20">
              <p className="text-sm font-bold text-white break-words">{investor.investorName}</p>
              {investor.companyName && (
                <p className="text-xs text-gray-400 truncate">{investor.companyName}</p>
              )}
            </div>
            <span className="absolute top-0 right-0 text-xs text-emerald-400 underline">
              View profile
            </span>
          </div>
          <div className="space-y-2 mt-3">
            <DealTracker userType={USER_TYPES.startup} status={baseStatus} />
          </div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            type="button"
            onClick={() => setRevealed(true)}
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm px-5 py-2 rounded-full transition-colors shadow-lg"
          >
            Click to view
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-700 bg-[#1A1A1A] p-4 space-y-4">
      <div>
        <p className="text-sm text-emerald-400 font-medium mb-2">
          The following investor is interested:
        </p>
        <div className="space-y-0.5 text-sm text-left mb-2">
          <p className="text-white">
            <span className="text-gray-400">Investor name: </span>
            {investor.investorName}
          </p>
          {investor.companyName && (
            <p className="text-white">
              <span className="text-gray-400">Company: </span>
              {investor.companyName}
            </p>
          )}
          <p className="text-white">
            <span className="text-gray-400">Location: </span>
            {investor.country}
          </p>
        </div>
      </div>

      <RadioQuestion
        label="Do you know them already?"
        name={`knows-${investor.investorId}`}
        value={knowsInvestor}
        onChange={setKnowsInvestor}
      />

      <RadioQuestion
        label="Have you pitched to them in the last 6 months?"
        name={`pitched-${investor.investorId}`}
        value={pitchedRecently}
        onChange={setPitchedRecently}
      />

      {error && <p className="text-xs text-red-400">{error}</p>}

      <Button
        variant="primary"
        disabled={!canSubmit}
        onClick={handleSubmit}
        className="text-sm py-1.5"
      >
        {submitting ? (
          <span className="inline-flex items-center gap-2">
            <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Submitting…
          </span>
        ) : (
          'Submit'
        )}
      </Button>
    </div>
  );
};

function RadioQuestion({
  label,
  name,
  value,
  onChange,
}: {
  label: string;
  name: string;
  value: ResponseAnswer | null;
  onChange: (v: ResponseAnswer) => void;
}) {
  return (
    <fieldset>
      <legend className="text-sm font-bold text-gray-300 mb-1.5">{label}</legend>
      <div className="flex gap-4 justify-center">
        {RADIO_OPTIONS.map(opt => (
          <label
            key={opt.value}
            className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-300 hover:text-white transition-colors"
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="accent-emerald-500 w-3.5 h-3.5"
            />
            {opt.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default InterestedInvestorCard;
